import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useWizardStore } from "@/store/webinar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { DateTimePicker } from "@/components/webinar/agendamento/DateTimePicker";
import { TimezoneSelector } from "@/components/webinar/agendamento/TimezoneSelector";
import { AgendamentoPreview } from "@/components/webinar/agendamento/AgendamentoPreview";
import { addMinutes } from "date-fns";

const DAYS_OF_WEEK = [
  { value: 'mon', get label() { return translateInterfaceText("Seg"); } },
  { value: 'tue', get label() { return translateInterfaceText("Ter"); } },
  { value: 'wed', get label() { return translateInterfaceText("Qua"); } },
  { value: 'thu', get label() { return translateInterfaceText("Qui"); } },
  { value: 'fri', get label() { return translateInterfaceText("Sex"); } },
  { value: 'sat', get label() { return translateInterfaceText("Sáb"); } },
  { value: 'sun', get label() { return translateInterfaceText("Dom"); } },
];

const INTERVAL_OPTIONS = [
  { value: 5, get label() { return translateInterfaceText("5 minutos"); } },
  { value: 10, get label() { return translateInterfaceText("10 minutos"); } },
  { value: 15, get label() { return translateInterfaceText("15 minutos"); } },
  { value: 30, get label() { return translateInterfaceText("30 minutos"); } },
];

export function AgendamentoStep() {
  useLanguage();
  const { data, updateData, updateNestedData } = useWizardStore();

  const handleModeChange = (mode: 'scheduled' | 'jit') => {
    updateData('schedule_mode', mode);
  };

  const handleScheduledChange = (field: string, value: any) => {
    updateNestedData('scheduled_config', field, value);
  };

  const handleJITChange = (field: string, value: any) => {
    updateNestedData('jit_config', field, value);
  };

  const handleDayToggle = (day: string, checked: boolean) => {
    const currentDays = data.jit_config?.days_of_week || [];
    const newDays = checked
      ? [...currentDays, day]
      : currentDays.filter(d => d !== day);
    handleJITChange('days_of_week', newDays);
  };

  const minDate = addMinutes(new Date(), 10);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div>
          <h2 className="text-2xl font-bold">{translateInterfaceText("Agendamento")}</h2>
          <p className="text-muted-foreground mt-1">
            {translateInterfaceText("Defina quando e como as sessões acontecem")}</p>
        </div>

        <Tabs value={data.schedule_mode} onValueChange={handleModeChange}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="scheduled">{translateInterfaceText("Sessão Agendada")}</TabsTrigger>
            <TabsTrigger value="jit">{translateInterfaceText("Just-in-Time")}</TabsTrigger>
          </TabsList>

          {/* SESSÃO AGENDADA */}
          <TabsContent value="scheduled" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{translateInterfaceText("Data e Horário")}</CardTitle>
                <CardDescription>
                  {translateInterfaceText("Defina a data, duração e fuso. Nós cuidamos do resto.")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <DateTimePicker
                  label={translateInterfaceText("Início da Sessão")}
                  value={data.scheduled_config?.start_at || ''}
                  onChange={(value) => handleScheduledChange('start_at', value)}
                  minDate={minDate}
                />

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{translateInterfaceText("Duração (minutos)")}</Label>
                    <span className="text-sm font-medium">
                      {data.scheduled_config?.duration_min || 60} {" "}{translateInterfaceText("min")}</span>
                  </div>
                  <Slider
                    value={[data.scheduled_config?.duration_min || 60]}
                    onValueChange={([value]) => handleScheduledChange('duration_min', value)}
                    min={15}
                    max={480}
                    step={15}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    {translateInterfaceText("Entre 15 e 480 minutos")}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateInterfaceText("Fuso Horário")}</CardTitle>
              </CardHeader>
              <CardContent>
                <TimezoneSelector
                  value={data.scheduled_config?.timezone || 'America/Sao_Paulo'}
                  onChange={(value) => handleScheduledChange('timezone', value)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateInterfaceText("Capacidade e Acesso")}</CardTitle>
                <CardDescription>
                  {translateInterfaceText("Configure limites e regras de entrada")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{translateInterfaceText("Limite de inscrições (opcional)")}</Label>
                  <Input
                    type="number"
                    min={0}
                    placeholder={translateInterfaceText("Deixe vazio para ilimitado")}
                    value={data.scheduled_config?.capacity || ''}
                    onChange={(e) => handleScheduledChange('capacity', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{translateInterfaceText("Permitir entrada até (minutos após início)")}</Label>
                  <Input
                    type="number"
                    min={0}
                    max={60}
                    value={data.scheduled_config?.late_join_minutes || 15}
                    onChange={(e) => handleScheduledChange('late_join_minutes', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    {translateInterfaceText("Usuários podem entrar até")}{" "}{data.scheduled_config?.late_join_minutes || 15} {" "}{translateInterfaceText("minutos após o início")}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* JUST-IN-TIME */}
          <TabsContent value="jit" className="space-y-4 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{translateInterfaceText("Calendário de Disponibilidade")}</CardTitle>
                <CardDescription>
                  {translateInterfaceText("Sempre uma próxima sessão em poucos minutos")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{translateInterfaceText("Dias da semana")}</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map((day) => (
                      <div key={day.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={day.value}
                          checked={data.jit_config?.days_of_week?.includes(day.value)}
                          onCheckedChange={(checked) => handleDayToggle(day.value, checked as boolean)}
                        />
                        <Label htmlFor={day.value} className="cursor-pointer font-normal">
                          {day.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateInterfaceText("Janela Diária")}</CardTitle>
                <CardDescription>
                  {translateInterfaceText("Horário de funcionamento das sessões")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{translateInterfaceText("Início do dia")}</Label>
                    <Input
                      type="time"
                      value={data.jit_config?.day_start || '08:00'}
                      onChange={(e) => handleJITChange('day_start', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{translateInterfaceText("Fim do dia")}</Label>
                    <Input
                      type="time"
                      value={data.jit_config?.day_end || '22:00'}
                      onChange={(e) => handleJITChange('day_end', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateInterfaceText("Intervalos")}</CardTitle>
                <CardDescription>
                  {translateInterfaceText("Frequência de geração de sessões")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{translateInterfaceText("Intervalo entre sessões")}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {INTERVAL_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleJITChange('interval_min', option.value)}
                        className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                          data.jit_config?.interval_min === option.value
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background hover:bg-muted border-input'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{translateInterfaceText("Tempo de espera mínimo (minutos)")}</Label>
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    value={data.jit_config?.min_wait_min || 5}
                    onChange={(e) => handleJITChange('min_wait_min', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    {translateInterfaceText("Próxima sessão aparecerá daqui a pelo menos")}{" "}{data.jit_config?.min_wait_min || 5} {" "}{translateInterfaceText("minutos")}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateInterfaceText("Horários de Silêncio")}</CardTitle>
                <CardDescription>
                  {translateInterfaceText("Evite ofertar sessões em horários sensíveis")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="quiet-hours"
                    checked={!!data.jit_config?.quiet_hours}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        handleJITChange('quiet_hours', { from: '00:00', to: '06:00' });
                      } else {
                        handleJITChange('quiet_hours', undefined);
                      }
                    }}
                  />
                  <Label htmlFor="quiet-hours" className="cursor-pointer">
                    {translateInterfaceText("Ativar horários de silêncio")}</Label>
                </div>

                {data.jit_config?.quiet_hours && (
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="space-y-2">
                      <Label>{translateInterfaceText("De")}</Label>
                      <Input
                        type="time"
                        value={data.jit_config.quiet_hours.from}
                        onChange={(e) => handleJITChange('quiet_hours', {
                          ...data.jit_config.quiet_hours,
                          from: e.target.value
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{translateInterfaceText("Até")}</Label>
                      <Input
                        type="time"
                        value={data.jit_config.quiet_hours.to}
                        onChange={(e) => handleJITChange('quiet_hours', {
                          ...data.jit_config.quiet_hours,
                          to: e.target.value
                        })}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateInterfaceText("Exibição e Capacidade")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{translateInterfaceText("Quantos horários mostrar ao visitante")}</Label>
                  <div className="flex gap-2">
                    {[1, 3, 5].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => handleJITChange('display_slots', num)}
                        className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                          data.jit_config?.display_slots === num
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background hover:bg-muted border-input'
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{translateInterfaceText("Capacidade por slot (opcional)")}</Label>
                  <Input
                    type="number"
                    min={0}
                    placeholder={translateInterfaceText("Deixe vazio para ilimitado")}
                    value={data.jit_config?.capacity_per_slot || ''}
                    onChange={(e) => handleJITChange('capacity_per_slot', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateInterfaceText("Fuso Horário")}</CardTitle>
              </CardHeader>
              <CardContent>
                <TimezoneSelector
                  value={data.jit_config?.timezone || 'America/Sao_Paulo'}
                  onChange={(value) => handleJITChange('timezone', value)}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* PREVIEW */}
      <div className="hidden lg:block">
        <div className="sticky top-6">
          <AgendamentoPreview
            mode={data.schedule_mode}
            scheduledConfig={data.scheduled_config}
            jitConfig={data.jit_config}
          />
        </div>
      </div>
    </div>
  );
}
